import { call, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import moment from 'moment';
import { gcApi } from 'config/axios';
import Personalizer from 'personalizer';
import Localizer from 'localizer';
import DateFormatter from 'utils/date/DateFormatter';
import DateParser from 'utils/date/DateParser';
import { formatTypeValue } from '../GroupPage/ChallengeTab/utils/GroupChallengeUtils';

/** redux selector */
import { selectGroup, selectViewerSocialProfile, selectGroupCurrentChallenge } from 'reducers/group/groupPage';
import { selectGroupLeaderboardType, selectGroupLeaderboardViewPrevious } from 'reducers/group/groupPageLeaderboard';

/** redux actions */
import { getUserPreference, saveUserPreference } from 'reducers/preference/preference';
import { getActivityTypes, isActivityTypesFetching } from 'reducers/activity/activityTypes';
import {
    fetchGroupLeaderboardSuccess,
    fetchGroupLeaderboardFailed,
    fetchGroupLeaderboard,
    setGroupLeaderboardType
} from '../actions/groupPageActions';
import { fetchMultipleUserSocialProfileImage } from 'actions/userSocialProfileImageActions';

import {
    FETCH_GROUP_LEADERBOARD,
    SET_GROUP_LEADERBOARD_TYPE,
    INIT_GROUP_LEADERBOARD,
    SET_GROUP_LEADERBOARD_VIEW_PREVIOUS,
    FETCH_GROUP_CHALLENGE_LEADERBOARD,
    FETCH_GROUP_CHALLENGE_LEADERBOARD_START,
    FETCH_GROUP_CHALLENGE_LEADERBOARD_SUCCESS,
    FETCH_GROUP_CHALLENGE_LEADERBOARD_FAILED,
    RECEIVE_ACTIVITY_TYPES,
    ACTIVITY_TYPES_FAIL
} from '../actions/ActionTypes';

import '../actions/groupPageActionsTypeDefs';

const prefToLeaderboardType = {
    WELLNESS_TOTAL_STEPS: 'TOTAL_STEPS',
    walking: 'WALKING_DISTANCE',
    running: 'RUNNING_DISTANCE',
    cycling: 'CYCLING_DISTANCE',
    swimming: 'SWIMMING_DISTANCE',
    total: 'TOTAL_DISTANCE',
    calories: 'ACTIVE_CALORIES_BURNED',
    para_sports: 'PUSHING_DISTANCE'
};

const leaderboardTypeToPref = {
    TOTAL_STEPS: 'WELLNESS_TOTAL_STEPS',
    WALKING_DISTANCE: 'walking',
    RUNNING_DISTANCE: 'running',
    CYCLING_DISTANCE: 'cycling',
    SWIMMING_DISTANCE: 'swimming',
    TOTAL_DISTANCE: 'total',
    ACTIVE_CALORIES_BURNED: 'calories',
    TOTAL_PUSHES: 'WELLNESS_TOTAL_PUSHES',
    PUSHING_DISTANCE: 'para_sports'
};

const leaderboardApiSettings = {
    TOTAL_STEPS: {
        metricId: 29,
        userType: 'wellness',
        metricKey: 'WELLNESS_TOTAL_STEPS'
    },
    WALKING_DISTANCE: {
        activityTypeKey: 'walking',
        metricId: 17,
        userType: 'activity',
        metricKey: 'ACTIVITY_TOTAL_DISTANCE'
    },
    RUNNING_DISTANCE: {
        activityTypeKey: 'running',
        metricId: 17,
        userType: 'activity',
        metricKey: 'ACTIVITY_TOTAL_DISTANCE'
    },
    CYCLING_DISTANCE: {
        activityTypeKey: 'cycling',
        metricId: 17,
        userType: 'activity',
        metricKey: 'ACTIVITY_TOTAL_DISTANCE'
    },
    SWIMMING_DISTANCE: {
        activityTypeKey: 'swimming',
        metricId: 17,
        userType: 'activity',
        metricKey: 'ACTIVITY_TOTAL_DISTANCE'
    },
    TOTAL_DISTANCE: {
        metricId: 17,
        userType: 'activity',
        metricKey: 'ACTIVITY_TOTAL_DISTANCE'
    },
    ACTIVE_CALORIES_BURNED: {
        metricId: 22,
        userType: 'wellness',
        metricKey: 'WELLNESS_ACTIVE_CALORIES'
    },
    TOTAL_PUSHES: {
        metricId: 87,
        userType: 'wellness',
        metricKey: 'WELLNESS_TOTAL_PUSHES'
    },
    PUSHING_DISTANCE: {
        activityTypeKey: 'para_sports',
        metricId: 17,
        userType: 'activity',
        metricKey: 'ACTIVITY_TOTAL_DISTANCE'
    }
};

export function formatValueSteps(distanceValue) {
    return Personalizer.personalizeSteps(distanceValue);
}

export function formatValueDistance(distanceValue) {
    return (
        Personalizer.personalizeDistance(distanceValue) + ' ' + Localizer.localize(Personalizer.getDistanceUnitKey())
    );
}

export function formatValueSwimming(distanceValue) {
    return (
        Personalizer.personalizeSwimDistance(distanceValue) +
        ' ' +
        Localizer.localize(Personalizer.getSwimDistanceUnitKey())
    );
}

export function formatValueCalories(distanceValue) {
    return Personalizer.personalizeCaloriesNotConvert(distanceValue) + ' ' + Localizer.localize('label_calories_abbr');
}

export function formatValuePushes(value) {
    return Personalizer.formatWholeNumber(value);
}

export function* initGroupLeaderboardSaga() {
    const userPrefValue = yield select(getUserPreference, 'group.page.leaderboard.filter');
    const group = yield select(selectGroup);
    const leaderboardType = yield select(selectGroupLeaderboardType);

    if (leaderboardType && group.leaderboardTypes.includes(leaderboardType)) {
        /** inited, ignore */
        return;
    }

    let defaultLeaderboardType = prefToLeaderboardType[userPrefValue];

    if (!group.leaderboardTypes.includes(defaultLeaderboardType)) {
        defaultLeaderboardType = group.leaderboardTypes[0];
    }
    const activityTypes = yield select(getActivityTypes);

    if (!activityTypes || activityTypes.length === 0) {
        let activityTypesFetching = yield select(isActivityTypesFetching);
        do {
            yield take([RECEIVE_ACTIVITY_TYPES, ACTIVITY_TYPES_FAIL]);
            activityTypesFetching = yield select(isActivityTypesFetching);
        } while (activityTypesFetching);
    }

    yield put(setGroupLeaderboardType({ leaderboardType: defaultLeaderboardType }));
}

export function* fetchGroupLeaderboardSaga() {
    const userPrefValue = yield select(getUserPreference, 'group.page.leaderboard.filter');

    const group = yield select(selectGroup);
    const leaderboardType = yield select(selectGroupLeaderboardType);
    const viewPrevious = yield select(selectGroupLeaderboardViewPrevious);
    const viewerSocialProfile = yield select(selectViewerSocialProfile);
    const activityTypes = yield select(getActivityTypes);

    const newUserPrefValue = leaderboardTypeToPref[leaderboardType];

    if (newUserPrefValue !== userPrefValue) {
        yield put(saveUserPreference('group.page.leaderboard.filter', newUserPrefValue));
    }

    const settings = leaderboardApiSettings[leaderboardType];

    /** TODO: replace moment to other library */
    const todayDate = moment().format('YYYY-MM-DD');
    let startDate = moment().startOf('isoWeek');
    let endDate = moment().endOf('isoWeek');

    if (viewPrevious) {
        startDate = startDate.subtract(7, 'day');
        endDate = endDate.subtract(7, 'day');
    }

    const params = {
        start: 1,
        limit: 2000,
        todayDate,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        metricId: settings.metricId
    };

    const actType = activityTypes.find((item) => item.typeKey === settings.activityTypeKey);

    if (actType) {
        params.actTypeId = actType.typeId;
    }

    try {
        const response = yield call(
            gcApi.get,
            `/groupchallenge-service/leaderboard/${settings.userType}/group/${group.id}`,
            { params }
        );

        let list = response.data.allMetrics.metricsMap[settings.metricKey] || [];

        const viewerDisplayName = viewerSocialProfile && viewerSocialProfile.displayName;

        let rank = 0;
        let lastValue = Number.MAX_VALUE;

        let formatValue;

        switch (leaderboardType) {
            case 'TOTAL_STEPS':
                formatValue = formatValueSteps;
                break;
            case 'WALKING_DISTANCE':
            case 'RUNNING_DISTANCE':
            case 'CYCLING_DISTANCE':
            case 'TOTAL_DISTANCE':
            case 'PUSHING_DISTANCE':
                formatValue = formatValueDistance;
                break;
            case 'SWIMMING_DISTANCE':
                formatValue = formatValueSwimming;
                break;
            case 'ACTIVE_CALORIES_BURNED':
                formatValue = formatValueCalories;
                break;
            case 'WELLNESS_PUSHES_COUNT':
                formatValue = formatValuePushes;
                break;
            default:
                formatValue = (value) => `${value}`;
                break;
        }

        list = list.filter((item) => {
            return (
                item.value > 0 ||
                (item.value === 0 && item.hasPrivate) ||
                item.userInfo.displayname === viewerDisplayName
            );
        });

        list = list.map((item, index) => {
            if (lastValue > item.value) {
                rank = index + 1;
                lastValue = item.value;
            }
            const value = formatValue(item.value || 0);
            const todayValue = formatValue(item.todayValue || 0);
            const highlight = item.userInfo.displayname === viewerDisplayName;

            let lastSyncTime = '--';

            if (item.lastSyncTime) {
                const date = new Date(item.lastSyncTime);
                lastSyncTime = DateFormatter.formatRelativeTimeLastSync(date);
            }

            return {
                ...item,
                value,
                todayValue,
                lastSyncTime,
                rank,
                highlight
            };
        });
        yield put(fetchGroupLeaderboardSuccess({ list }));

        yield fork(fetchLeaderboardProfileImageAndFrameSaga, response.data, settings.metricKey);
    } catch (error) {
        yield put(fetchGroupLeaderboardFailed());
    }
}

export function* leaderboardCriteriaChangedSaga() {
    yield put(fetchGroupLeaderboard());
}

export function* fetchGroupChallengeLeaderboardSaga() {
    try {
        yield put({
            type: FETCH_GROUP_CHALLENGE_LEADERBOARD_START
        });

        const challenge = yield select(selectGroupCurrentChallenge);
        const { uuid, groupChallengeActivityTypeId } = challenge;
        const viewerSocialProfile = yield select(selectViewerSocialProfile);
        const viewerDisplayName = viewerSocialProfile && viewerSocialProfile.displayName;

        const today = new Date();
        const todayDate = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;

        const params = {
            start: 1,
            limit: 100,
            todayDate
        };

        const response = yield call(gcApi.get, `/groupchallenge-service/groupchallenge/${uuid}/players`, { params });

        // manipulate reponse data to fit in Leaderboard component
        const data = response.data.map(
            ({
                displayName,
                fullName,
                profileImageMedium,
                profileImageSmall,
                userProfileId,
                isPartialResultSet,
                lastSyncTime,
                ranking,
                todayNumber,
                totalNumber
            }) => {
                let formattedLastSyncTime = '--';
                if (lastSyncTime) {
                    const lastSyncUTCTime = DateParser.parseISOUTC(lastSyncTime);
                    formattedLastSyncTime = DateFormatter.formatRelativeTimeLastSync(lastSyncUTCTime);
                }
                return {
                    userInfo: {
                        displayname: displayName,
                        fullname: fullName,
                        profileImageUrlMedium: profileImageMedium,
                        profileImageUrlSmall: profileImageSmall,
                        userProfilePk: userProfileId
                    },
                    hasPrivate: isPartialResultSet,
                    lastSyncTime: formattedLastSyncTime,
                    todayValue: formatTypeValue(groupChallengeActivityTypeId, todayNumber),
                    value: formatTypeValue(groupChallengeActivityTypeId, totalNumber),
                    rank: ranking,
                    highlight: displayName === viewerDisplayName
                };
            }
        );

        yield put({
            type: FETCH_GROUP_CHALLENGE_LEADERBOARD_SUCCESS,
            data
        });

        yield fork(fetchChallengeLeaderboardProfileImageAndFrameSaga, data);
    } catch (error) {
        yield put({
            type: FETCH_GROUP_CHALLENGE_LEADERBOARD_FAILED
        });
    }
}

/**
 *
 * @param {Object} responseData
 * @param {Object} responseData.allMetrics
 * @param {Object} responseData.allMetrics.metricsMap
 * @param {string} metricKey
 * @returns
 */
function* fetchLeaderboardProfileImageAndFrameSaga(responseData, metricKey) {
    if (!responseData?.allMetrics?.metricsMap) {
        return;
    }
    /**
     * @type {{ userInfo: { userProfilePk: number } }[]}
     */
    const leaderboardList = responseData.allMetrics.metricsMap[metricKey];

    if (!leaderboardList || !leaderboardList.length) {
        return;
    }
    const userIds = leaderboardList.map((item) => item.userInfo.userProfilePk);

    yield fork(fetchProfileImageAndFrameSaga, userIds);
}

/**
 *
 * @param {{ userInfo: { userProfilePk: number } }[]} leaderboardList
 * @returns
 */
function* fetchChallengeLeaderboardProfileImageAndFrameSaga(leaderboardList) {
    if (!leaderboardList || !leaderboardList.length) {
        return;
    }
    const userIds = leaderboardList.map((item) => item.userInfo.userProfilePk);

    yield fork(fetchProfileImageAndFrameSaga, userIds);
}

/** fetch profile image and frame */
export function* fetchProfileImageAndFrameSaga(userIds) {
    if (!userIds || !userIds.length) {
        return;
    }
    yield put(fetchMultipleUserSocialProfileImage(userIds));
}

// watcher
export const groupPageLeaderboardSagas = [
    takeLatest(INIT_GROUP_LEADERBOARD, initGroupLeaderboardSaga),
    takeLatest(FETCH_GROUP_LEADERBOARD, fetchGroupLeaderboardSaga),
    takeLatest([SET_GROUP_LEADERBOARD_TYPE, SET_GROUP_LEADERBOARD_VIEW_PREVIOUS], leaderboardCriteriaChangedSaga),
    takeLatest(FETCH_GROUP_CHALLENGE_LEADERBOARD, fetchGroupChallengeLeaderboardSaga)
];
